// Catalog of real health-data sources SARA can integrate. Connection is mocked for the demo,
// but every provider, the data it exposes, and its auth method are real and current (2026).
import {
  Hospital, Watch, Activity, HeartPulse, Scale, BedDouble, Cable, CalendarDays, type LucideIcon,
} from 'lucide-react'

export type Provider = {
  id: string
  name: string
  blurb: string
  data: string[]
  auth: string // real auth mechanism
  note?: string // current (2026) caveat shown subtly
  accent: string // css var
  connected?: boolean
  lastSync?: string
  real?: boolean // true = wired to a real OAuth backend flow (not the mock modal)
  oauthProvider?: string // backend provider key for /v1/integrations/<provider>/authorize
}

export type Category = {
  id: string
  label: string
  hint: string
  icon: LucideIcon
  providers: Provider[]
}

export const CATALOG: Category[] = [
  {
    id: 'calendar',
    label: 'Calendar & life context',
    hint: 'So SARA can coach around your real week — travel, big days, late meetings.',
    icon: CalendarDays,
    providers: [
      { id: 'google-calendar', name: 'Google Calendar', blurb: 'Your upcoming events & travel', data: ['Upcoming events', 'Travel', 'Meetings'], auth: 'Google OAuth2 (calendar.readonly)', note: 'Real OAuth — live connection', accent: 'var(--info-text)', real: true, oauthProvider: 'google' },
    ],
  },
  {
    id: 'records',
    label: 'Health records & EMR',
    hint: 'Pull your clinical history, labs, meds and visits straight from your providers.',
    icon: Hospital,
    providers: [
      { id: 'epic', name: 'Epic MyChart', blurb: 'Your hospital/clinic record via Epic', data: ['Labs', 'Medications', 'Conditions', 'Visits', 'Immunizations'], auth: 'SMART on FHIR (OAuth2)', note: 'Largest US EHR footprint', accent: 'var(--info-text)', connected: true, lastSync: '2h ago' },
      { id: 'cerner', name: 'Oracle Health (Cerner)', blurb: 'Records from Cerner-based systems', data: ['Labs', 'Medications', 'Conditions', 'Encounters'], auth: 'SMART on FHIR (OAuth2, R4)', accent: 'var(--ornament-fig)' },
      { id: 'bluebutton', name: 'Medicare Blue Button 2.0', blurb: 'Medicare Parts A/B/D claims (CMS)', data: ['Claims', 'Medications', 'Procedures'], auth: 'OAuth2 (FHIR R4)', note: 'For Medicare beneficiaries', accent: 'var(--ornament-plum)' },
      { id: 'apple-records', name: 'Apple Health Records', blurb: 'Aggregated records on your iPhone', data: ['Labs', 'Medications', 'Conditions'], auth: 'HealthKit (on-device consent)', accent: 'var(--foreground)' },
      { id: '1up', name: '1upHealth', blurb: 'Connect thousands of provider portals', data: ['FHIR R4 clinical data'], auth: 'OAuth2 / SMART on FHIR', accent: 'var(--success-text)' },
      { id: 'flexpa', name: 'Flexpa', blurb: 'Payer + clinical records via TEFCA', data: ['Claims', 'Clinical (FHIR R4)'], auth: 'OAuth2 + IAL2 identity', note: 'Built-in consent & identity proofing', accent: 'var(--info-text)' },
    ],
  },
  {
    id: 'wearables',
    label: 'Wearables & fitness',
    hint: 'Activity, heart rate, recovery and readiness from the things you wear.',
    icon: Watch,
    providers: [
      { id: 'google-health', name: 'Google Health', blurb: 'Fitbit, Pixel Watch & Google health data', data: ['Steps', 'Heart rate', 'Sleep', 'Weight', 'Glucose'], auth: 'Google Health API (OAuth2)', note: 'Cloud successor to the Fitbit Web API', accent: 'var(--success-text)', real: true, oauthProvider: 'googlehealth' },
      { id: 'apple-watch', name: 'Apple Watch', blurb: 'Heart rate, activity, ECG, SpO₂', data: ['Heart rate', 'HRV', 'Steps', 'Workouts', 'ECG'], auth: 'HealthKit (on-device)', accent: 'var(--foreground)', connected: true, lastSync: '4m ago' },
      { id: 'oura', name: 'Oura Ring', blurb: 'Sleep, readiness, temperature', data: ['Sleep stages', 'Readiness', 'HRV', 'Temp'], auth: 'Oura API v2 (OAuth2)', note: 'OAuth2 only — PATs retired Dec 2025', accent: 'var(--ornament-plum)', connected: true, lastSync: '18m ago' },
      { id: 'whoop', name: 'WHOOP', blurb: 'Strain, recovery, sleep', data: ['Recovery', 'Strain', 'Sleep', 'HRV'], auth: 'WHOOP API (OAuth2 + webhooks)', note: 'Real OAuth — live connection', accent: 'var(--foreground)', real: true, oauthProvider: 'whoop' },
      { id: 'garmin', name: 'Garmin', blurb: 'Activity, stress, body battery', data: ['Activity', 'Stress', 'Pulse Ox', 'Sleep'], auth: 'Garmin Health API (OAuth2, push)', accent: 'var(--info-text)' },
      { id: 'fitbit', name: 'Fitbit', blurb: 'Steps, sleep, heart rate', data: ['Steps', 'Sleep', 'Heart rate', 'SpO₂'], auth: 'Fitbit Web API (OAuth2)', note: 'Sunsetting Sept 2026 → Google Health API', accent: 'var(--info-text)' },
      { id: 'health-connect', name: 'Google Health Connect', blurb: 'Android on-device health hub', data: ['Steps', 'Heart rate', 'Sleep'], auth: 'Health Connect (on-device)', accent: 'var(--success-text)' },
      { id: 'withings', name: 'Withings', blurb: 'Activity, sleep, weight, BP', data: ['Activity', 'Sleep', 'Weight', 'BP'], auth: 'Withings API (OAuth2)', accent: 'var(--info-text)' },
    ],
  },
  {
    id: 'cgm',
    label: 'Continuous glucose (CGM)',
    hint: 'Real-time glucose trends — central for metabolic and diabetes coaching.',
    icon: Activity,
    providers: [
      { id: 'dexcom', name: 'Dexcom', blurb: 'G6/G7 continuous glucose', data: ['Glucose (5-min)', 'Trends', 'Time-in-range'], auth: 'Dexcom API v3 (OAuth2)', note: 'CGM data is PHI', accent: 'var(--success-text)', connected: true, lastSync: '5m ago' },
      { id: 'libre', name: 'Abbott FreeStyle Libre', blurb: 'LibreView glucose share', data: ['Glucose', 'Trends'], auth: 'via Terra / LibreLinkUp', note: 'No official public API — via aggregator', accent: 'var(--warning-text)' },
      { id: 'levels', name: 'Levels', blurb: 'Metabolic scores on CGM', data: ['Glucose', 'Metabolic score'], auth: 'via underlying CGM (Dexcom/Libre)', note: 'No public API of its own', accent: 'var(--ornament-clay)' },
    ],
  },
  {
    id: 'bp',
    label: 'Blood pressure',
    hint: 'At-home cuffs feed BP trends — key for your pre-hypertension coaching.',
    icon: HeartPulse,
    providers: [
      { id: 'omron', name: 'Omron Connect', blurb: 'Omron home BP monitors', data: ['Systolic', 'Diastolic', 'Pulse'], auth: 'OMRON Connect Create API', accent: 'var(--info-text)', connected: true, lastSync: '1d ago' },
      { id: 'withings-bpm', name: 'Withings BPM', blurb: 'Connected BP monitor', data: ['Systolic', 'Diastolic', 'Pulse', 'Weight'], auth: 'Withings API (OAuth2)', note: 'Real OAuth — live connection', accent: 'var(--info-text)', real: true, oauthProvider: 'withings' },
      { id: 'ihealth', name: 'iHealth', blurb: 'iHealth BP devices', data: ['Systolic', 'Diastolic'], auth: 'iHealth API (OAuth2)', accent: 'var(--ornament-fig)' },
    ],
  },
  {
    id: 'scale',
    label: 'Smart scale & body composition',
    hint: 'Weight, body fat and trends over time.',
    icon: Scale,
    providers: [
      { id: 'withings-body', name: 'Withings Body', blurb: 'Smart scale + body comp', data: ['Weight', 'Body fat', 'Muscle', 'BMI'], auth: 'Withings API (OAuth2)', note: 'Most developer-friendly scale API', accent: 'var(--info-text)' },
      { id: 'renpho', name: 'Renpho', blurb: 'Body composition scale', data: ['Weight', 'Body fat'], auth: 'via Terra aggregator', note: 'No official public API', accent: 'var(--ornament-clay)' },
    ],
  },
  {
    id: 'sleep',
    label: 'Sleep & smart bed',
    hint: 'Deep sleep tracking from your mattress or bed.',
    icon: BedDouble,
    providers: [
      { id: 'eight', name: 'Eight Sleep', blurb: 'Pod smart mattress cover', data: ['Sleep stages', 'HRV', 'Bed temp', 'Resp. rate'], auth: 'via Terra (account link)', note: 'No official public API', accent: 'var(--ornament-plum)' },
      { id: 'withings-sleep', name: 'Withings Sleep', blurb: 'Under-mattress sleep sensor', data: ['Sleep stages', 'Snoring', 'Heart rate'], auth: 'Withings API (OAuth2)', accent: 'var(--info-text)' },
      { id: 'sleep-number', name: 'Sleep Number', blurb: 'SleepIQ smart bed', data: ['Sleep score', 'Heart rate', 'Breathing'], auth: 'via aggregator', note: 'No official public program', accent: 'var(--info-text)' },
    ],
  },
  {
    id: 'aggregators',
    label: 'Universal connectors',
    hint: 'One integration, hundreds of devices — for power users and clinics.',
    icon: Cable,
    providers: [
      { id: 'terra', name: 'Terra', blurb: '500+ wearables via one API', data: ['Unified wearable data'], auth: 'OAuth2 + webhooks', note: 'Covers Eight Sleep, Libre, Renpho…', accent: 'var(--ornament-fig)' },
      { id: 'junction', name: 'Junction', blurb: 'Wearables + lab testing API', data: ['Wearables', 'Lab orders & results'], auth: 'OAuth2 + webhooks', note: 'Formerly Vital', accent: 'var(--success-text)' },
      { id: 'spike', name: 'Spike', blurb: 'Wearable data API, real-time', data: ['Unified wearable data'], auth: 'OAuth2 + webhooks', accent: 'var(--ornament-plum)' },
      { id: 'validic', name: 'Validic', blurb: 'Wearables + EHR-integrated RPM', data: ['Wearables', 'RPM streams'], auth: 'OAuth2', note: 'Enterprise / RPM heritage', accent: 'var(--info-text)' },
    ],
  },
]
